import { useCallback } from 'react';
import { useFileManager } from '@/hooks/useFileManager';
import { StatusIndicator } from './StatusIndicator';
import { useAppContext } from '@/context/AppContext';
import { DownloadMergeScript } from './DownloadMergeScript';

export function FileManager() {
  const [fileState, fileActions] = useFileManager();
  const { state, dispatch } = useAppContext();

  const handleXMLLoad = useCallback(async () => {
    if (!fileActions.checkUnsavedChanges()) return;
    await fileActions.loadXMLFile();
  }, [fileActions]);

  const handleSampleXMLLoad = useCallback(async () => {
    if (!fileActions.checkUnsavedChanges()) return;
    await fileActions.loadSampleXMLFile();
  }, [fileActions]);

  const handleLuaLoad = useCallback(async () => {
    await fileActions.loadLuaFile();
  }, [fileActions]);

  const handleSampleLuaLoad = useCallback(async () => {
    await fileActions.loadSampleLuaFile();
  }, [fileActions]);

  const handleCreateDebugLayout = useCallback(() => {
    if (!fileState.xmlDoc) {
      return;
    }

    if (!fileActions.checkUnsavedChanges()) {
      return;
    }

    const confirmed = window.confirm(
      'This will remove every vehicle from all vehicle groups and replace them with a single common vehicle per group. Continue?'
    );

    if (!confirmed) {
      return;
    }

    const xmlDoc = fileState.xmlDoc;
    const groupTypes: Array<'vehGroups'> = ['vehGroups'];
    const preferredVehicleModels = [
      'asea',
      'asterope',
      'blista',
      'bobcatxl',
      'buccaneer',
      'buffalo',
      'cavalcade',
      'chino',
      'coach',
      'dilettante',
      'dominator',
      'dubsta',
      'emperor',
      'emperor2',
      'faggio',
      'fugitive',
      'futo',
      'glendale',
      'granger',
      'gresley',
      'habanero',
      'ingot',
      'intruder',
      'issi2',
      'jackal',
      'landstalker',
      'mesa',
      'minivan',
      'oracle',
      'oracle2',
      'patriot',
      'penumbra',
      'peyote',
      'phoenix',
      'picador',
      'prairie',
      'premier',
      'primo',
      'radi',
      'rancherxl',
      'regina',
      'rocoto',
      'sabregt',
      'schafter2',
      'seminole',
      'stanier',
      'stratum',
      'sultan',
      'surge',
      'tailgater',
      'tornado',
      'washington',
      'youga',
      'zion',
      'zion2'
    ];


    const availableVehicleModels = Array.from(new Set(state.allModelsByGroup.vehGroups));
    const commonVehicles = preferredVehicleModels.filter(model => availableVehicleModels.includes(model));

    const modelPools = {
      vehGroups: commonVehicles.length > 0 ? commonVehicles : availableVehicleModels
    };

    let reusedModels = false;
    let missingModels = false;

    groupTypes.forEach((groupType) => {
      const groups = Array.from(xmlDoc.querySelectorAll(`${groupType} > Item`));
      const excludedGroupNames = new Set(['veh_prologue', 'veh_prologue_mp']);
      const pool = modelPools[groupType];

      if (pool.length === 0) {
        missingModels = true;
        return;
      }

      const eligibleGroups = groups.filter((groupElement) => {
        const nameElement = groupElement.querySelector('Name') || groupElement.querySelector('n');
        const name = nameElement?.textContent || '';
        return !excludedGroupNames.has(name);
      });

      if (pool.length < eligibleGroups.length) {
        reusedModels = true;
      }

      eligibleGroups.forEach((groupElement, index) => {
        const modelsContainer = groupElement.querySelector('models');
        if (!modelsContainer) {
          return;
        }

        while (modelsContainer.firstChild) {
          modelsContainer.removeChild(modelsContainer.firstChild);
        }

        const modelName = pool[index % pool.length];
        const newModel = xmlDoc.createElement('Item');
        const newModelName = xmlDoc.createElement('Name');
        newModelName.textContent = modelName;
        newModel.appendChild(newModelName);
        const variations = xmlDoc.createElement('Variations');
        newModel.appendChild(variations);
        modelsContainer.appendChild(newModel);
      });
    });

    dispatch({ type: 'SET_XML_DOC', payload: xmlDoc });
    dispatch({ type: 'MARK_UNSAVED' });

    if (missingModels) {
      dispatch({ type: 'ADD_ERROR', payload: 'No available vehicles found to populate the vehicle groups.' });
      return;
    }

    if (reusedModels) {
      dispatch({ type: 'ADD_ERROR', payload: 'Not enough unique vehicles to fill every group. Some vehicles were reused.' });
    }

    dispatch({ type: 'ADD_SUCCESS', payload: 'Created a debug layout with one common vehicle per group.' });
  }, [fileActions, fileState.xmlDoc, state.allModelsByGroup, dispatch]);

  return (
    <div className="file-management">
      <h2>File Management</h2>
      <div className="file-management-grid">
        <div className="file-management-column">
          <div className="file-panel">
            <div className="file-panel-header">
              <h3>Load popgroups</h3>
              <p>Open your `popgroups.ymt.pso.xml` and start editing vehicle groups.</p>
            </div>
            <div className="file-panel-actions file-panel-actions-row">
              <div className="file-panel-actions-group">
                <button
                  onClick={handleXMLLoad}
                  className={`file-btn ${fileState.xmlDoc ? 'file-btn-loaded' : 'file-btn-idle'}`}
                  disabled={fileState.loadingStates.xml}
                >
                  {fileState.loadingStates.xml ? 'Loading...' : <>Load <span className="vehicles-lua-filename">popgroups.ymt.pso.xml</span></>}
                </button>
                <button
                  onClick={handleSampleXMLLoad}
                  className="file-btn file-btn-secondary"
                  disabled={fileState.loadingStates.xml}
                >
                  Load sample
                </button>
              </div>
              <StatusIndicator
                type="xml"
                loading={fileState.loadingStates.xml}
                loaded={!!fileState.xmlDoc}
                filename={fileState.xmlFile?.name}
              />
            </div>
          </div>

          <div className="file-panel">
            <div className="file-panel-header">
              <h3>Load vehicles.lua</h3>
              <p>Import vehicle data from your server's vehicles.lua file. <br />This will add all the vehicles to the dropdowns, and you will see the price etc in the list.</p>
            </div>
            <div className="file-panel-actions file-panel-actions-row">
              <div className="file-panel-actions-group">
                <button
                  onClick={handleLuaLoad}
                  className={`file-btn ${fileState.luaFile ? 'file-btn-loaded' : 'file-btn-idle'}`}
                  disabled={fileState.loadingStates.lua}
                  title="Load Lua file with vehicle data (e.g., from qbx_core)"
                >
                  {fileState.loadingStates.lua ? 'Loading...' : <>Load <span className="vehicles-lua-filename">vehicles.lua</span></>}
                </button>
                <button
                  onClick={handleSampleLuaLoad}
                  className="file-btn file-btn-secondary"
                  disabled={fileState.loadingStates.lua}
                >
                  Load sample
                </button>
              </div>
              <StatusIndicator
                type="lua"
                loading={fileState.loadingStates.lua}
                loaded={fileState.vehicleData.size > 0}
                filename={fileState.luaFile?.name}
              />
            </div>
          </div>
        </div>

        <div className="file-management-column">
          <div className="file-panel file-panel-accent">
            <div className="file-panel-header">
              <h3>Spawn Debug Layout</h3>
              <p>Clears all vehicle groups and inserts one common vehicle per group so you can identify spawn zones.</p>
            </div>
            <div className="file-panel-actions file-panel-actions-stack">
              <button
                className="file-btn file-btn-warning"
                onClick={handleCreateDebugLayout}
                disabled={!fileState.xmlDoc}
              >
                Create Debug Layout
              </button>
            </div>
          </div>

          <div className="file-panel">
            <div className="file-panel-header">
              <h3>Vehicle Merge Script</h3>
              <p>Download a script to create a vehicles.lua file in the correct format if you have made changes to the stock qbx_core format.</p>
            </div>
            <div className="file-panel-actions file-panel-actions-stack">
              <DownloadMergeScript />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
